import FastLinkIntegration, {
  YodleeErrorData,
  YodleeExitData,
  YodleeSuccessData,
} from '@/components/yodlee/FastLinkIntegration';
import {
  Box,
  CircularProgress,
  DialogContent,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import {
  AggregatorFormStatus,
  AggregatorModel,
  useCreateYodleeCredentialsMutation,
  useLoadYodleeAccountsMutation,
  useLogYodleeErrorMutation,
  YodleeToken,
} from '@/store/yodleeApi';
import { IInstitution } from '../accountTypeData';
import {
  FinancialOptions,
  StepsForManual,
  StepsForRecommended,
} from '../useAddAccountNavigation';
import useAccountSave from '../useAccountSave';
import { UserContext } from '@/contexts/userContext';
import { useGetAdvisorGroupSettingsQuery } from '@/store/settingsApi';
import { useContext, useEffect, useState } from 'react';
import useGenerateLinks from './useGenerateLinks';
import { useGlobalSnackbar } from '@/contexts/snackContext';
import { useDispatch } from 'react-redux';
import {
  accountsApi,
  skipViewingInvestorTagForAccountsQuery,
} from '@/store/accountsApi';
import { useAttributeAccountsToViewingTag } from '@/hooks/useAttributeAccountsToViewingTag';
import type { AppDispatch } from '@/store/store';
import { resolveLinkedAccountIdsForAttribution } from '@/utils/viewingTagAccountAttribution';

async function listUnfilteredAccountIds(
  dispatch: AppDispatch,
): Promise<number[]> {
  const result = await dispatch(
    accountsApi.endpoints.getAccounts.initiate(
      {
        showClosed: false,
        ...skipViewingInvestorTagForAccountsQuery,
      },
      { forceRefetch: true },
    ),
  ).unwrap();
  return result.Records.map((account) => account.AccountId).filter(
    (id): id is number => typeof id === 'number' && Number.isFinite(id),
  );
}

/**
 * Best-effort snapshot for viewing-tag attribution. Returns `null` on failure
 * so callers skip attribution instead of treating a missed list as empty.
 */
async function safelyListUnfilteredAccountIds(
  dispatch: AppDispatch,
): Promise<number[] | null> {
  try {
    return await listUnfilteredAccountIds(dispatch);
  } catch (error) {
    console.error(
      '[RecommendedYodleeIntegration] failed to list accounts for tag attribution',
      error,
    );
    return null;
  }
}

interface RecommendedYodleeIntegrationProps {
  selected: IInstitution | null;
  updateSelected: (institution: IInstitution | null) => void;
  updateModalStep: (step: StepsForManual | StepsForRecommended | null) => void;
  updateModalExperience: (experience: FinancialOptions) => void;
  onClose?: () => void;
}

const RecommendedYodleeIntegration = ({
  selected,
  updateSelected,
  updateModalStep,
  onClose,
}: RecommendedYodleeIntegrationProps) => {
  const { showSnackbar } = useGlobalSnackbar();
  const dispatch = useDispatch<AppDispatch>();

  const { success, updateSuccess } = useAccountSave();
  const { attributableTagId, attributeAccountIds } =
    useAttributeAccountsToViewingTag();

  const { data: advisorGroupSettings } = useGetAdvisorGroupSettingsQuery();
  const { currentRole } = useContext(UserContext);
  const { shouldRenderContactSupport, shouldRenderSecurityLink, securityLink } =
    useGenerateLinks({ currentRole, advisorGroupSettings });

  const [createYodleeAccountError, setCreateYodleeAccountError] =
    useState<boolean>(false);
  // True while the FastLink handshake is being registered with the WA aggregator
  // (createYodleeCredentials -> loadYodleeAccounts). Drives the "we're working on
  // adding your account" message, which is critical for external "Open" banks
  // where the user returns to the app after OAuth and otherwise sees no feedback.
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isFastLinkLoading, setIsFastLinkLoading] = useState<boolean>(true);
  const [isProcessingHandoff, setIsProcessingHandoff] =
    useState<boolean>(false);

  const showPreloader =
    !createYodleeAccountError &&
    (isFastLinkLoading || isSaving || isProcessingHandoff);

  useEffect(() => {
    if (!isFastLinkLoading && !isSaving && !createYodleeAccountError) {
      setIsProcessingHandoff(false);
    }
  }, [isFastLinkLoading, isSaving, createYodleeAccountError]);

  // Yodlee API hooks
  const [createYodleeCredentials] = useCreateYodleeCredentialsMutation();
  const [loadYodleeAccounts] = useLoadYodleeAccountsMutation();
  const [logYodleeError] = useLogYodleeErrorMutation();

  // Copied from legacy.
  const isConnected = (model: AggregatorModel | undefined): boolean => {
    return (
      model?.CredentialsForm?.Status.StatusCode === AggregatorFormStatus.Success
    );
  };

  /**
   * Once Yodlee returns a successful FastLink session, register the connection
   * with the Wealth Access aggregator API. The backend requires two calls:
   *   1. POST /api/Yodlee/Credentials — creates or reuses the Wealth and returns its WealthId.
   *   2. GET  /api/Yodlee/Accounts/{wealthId}?YodleeToken=... — triggers the initial accounts load.
   */
  const saveYodleeAccount = async (
    data: YodleeSuccessData,
    institution: IInstitution,
    yodleeToken: YodleeToken,
  ) => {
    // Clear any stale error from a prior attempt so a retry shows the in-progress
    // message instead of the old error (the iframe is hidden while saving).
    setCreateYodleeAccountError(false);

    setIsSaving(true);
    try {
      const financialInstitutionId = Number(
        institution.institution?.FinancialInstitutionId,
      );
      const providerAccountId = Number(
        data.sites?.[0]?.providerAccountId ?? data.providerAccountId,
      );

      if (!financialInstitutionId || !providerAccountId) {
        throw new Error(
          `invalid Yodlee success payload: ${JSON.stringify({
            financialInstitutionId,
            providerAccountId,
          })}`,
        );
      }

      const accountIdsBeforeLink =
        attributableTagId != null
          ? await safelyListUnfilteredAccountIds(dispatch)
          : null;

      const credentialsResult = await createYodleeCredentials({
        FinancialInstitutionId: financialInstitutionId,
        ProviderAccountId: providerAccountId,
        AuthToken: yodleeToken.token,
      });

      if (credentialsResult.error || !credentialsResult.data.WealthId) {
        throw new Error('Yodlee credentials creation failed.');
      }

      const accountsResult = await loadYodleeAccounts({
        wealthId: credentialsResult.data.WealthId,
        YodleeToken: yodleeToken.token,
        IsUpdate: false,
      });

      if (accountsResult.error || !isConnected(accountsResult.data)) {
        throw new Error('Yodlee accounts load failed.');
      }

      dispatch(
        accountsApi.util.invalidateTags([{ type: 'Accounts', id: 'LIST' }]),
      );

      if (attributableTagId != null) {
        try {
          const accountIdsAfterLink =
            await safelyListUnfilteredAccountIds(dispatch);
          const newAccountIds = resolveLinkedAccountIdsForAttribution(
            accountIdsBeforeLink,
            accountIdsAfterLink,
          );
          if (newAccountIds != null) {
            await attributeAccountIds(newAccountIds);
          }
        } catch (error) {
          console.error(
            '[saveYodleeAccount] viewing-tag attribution failed',
            error,
          );
        }
      }

      updateSuccess(true);
      updateModalStep(StepsForRecommended.ACCOUNT_ADDED);
      showSnackbar(
        t('addAccountModal.submission.success', {
          name: institution.label,
        }),
      );
    } catch (error) {
      console.error('[saveYodleeAccount] failed', error);
      setIsSaving(false);
      setCreateYodleeAccountError(true);
      void logError(data);
    }
  };

  const logError = async (data: YodleeErrorData) => {
    if (!data.sites || data.sites.length === 0) {
      return;
    }

    const { providerAccountId, providerId, requestId, statusCode } =
      data.sites[0];

    const errorData = {
      providerAccountId,
      providerId,
      reference: requestId,
      status: statusCode,
    };

    // Silent failure.
    await logYodleeError(errorData);
  };

  const handleYodleeSuccess = async (
    data: YodleeSuccessData,
    institution: IInstitution,
    yodleeToken: YodleeToken,
  ) => {
    console.log('Yodlee success data:', data);
    console.log('yodleeToken', yodleeToken);

    await saveYodleeAccount(data, institution, yodleeToken);
  };

  const handleYodleeError = (
    data: YodleeErrorData,
    _institution: IInstitution,
    _yodleeToken: YodleeToken,
  ) => {
    console.log('handleYodleeError data :>> ', data);
    setIsProcessingHandoff(false);
    void logError(data);
  };

  const handleYodleeClose = (
    data: YodleeExitData,
    institution: IInstitution,
    yodleeToken: YodleeToken,
  ) => {
    console.log('Yodlee close data:', data);

    // Handle the close event based on the old codebase logic
    if (
      data.fnToCall === 'accountStatus' &&
      data.status !== 'USER_CLOSE_ACTION' &&
      data.sites &&
      data.sites[0]
    ) {
      const site = data.sites[0];

      if (site.status !== 'FAILED') {
        // This is actually a success case
        void handleYodleeSuccess(data, institution, yodleeToken);
        return;
      }

      // This is an error case
      if (!site.statusCode) {
        setIsProcessingHandoff(false);
        void logError(data);
        return;
      }
    }

    // User closed the dialog without completing
    if (!success) {
      updateSelected(null);
      onClose?.();
    }
  };

  if (!selected) {
    return null;
  }

  return (
    <>
      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            minHeight: 500,
            width: '100%',
          }}
        >
          {showPreloader && (
            <Stack
              alignItems='center'
              justifyContent='flex-start'
              gap={2}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 0,
              }}
            >
              <CircularProgress />
              {(isSaving || isProcessingHandoff) && (
                <>
                  <Typography variant='h6'>
                    {t('addAccountModal.accountBeingAdded')}
                  </Typography>
                  <Typography
                    variant='body2'
                    textAlign='center'
                  >
                    {t('addAccountModal.accountReadyNotification')}
                  </Typography>
                </>
              )}
            </Stack>
          )}

          {createYodleeAccountError && (
            <Stack
              alignItems='center'
              gap={2}
              sx={{
                position: 'relative',
                zIndex: 2,
                py: 4,
              }}
            >
              <Typography color='error'>
                {t('addAccountModal.submission.error', {
                  name: selected.label,
                })}
              </Typography>
            </Stack>
          )}

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: isSaving ? 'none' : 'block',
            }}
          >
            <FastLinkIntegration
              onError={handleYodleeError}
              onSuccess={handleYodleeSuccess}
              onClose={handleYodleeClose}
              onLoadingChange={setIsFastLinkLoading}
              onSessionProcessing={() => {
                setIsProcessingHandoff(true);
              }}
              institution={selected}
            />
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box
        sx={{
          borderTop: '1px solid var(--divider, rgba(0, 0, 0, 0.12))',
          padding: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center',
          }}
        >
          {shouldRenderContactSupport && (
            <>
              <Typography variant='body2'>
                {t('addAccountModal.needHelp')}{' '}
                <Link
                  href={selected.url || ''}
                  target='_blank'
                >
                  {t('addAccountModal.visitSite')}
                </Link>{' '}
                {t('or')} <Link>{t('addAccountModal.contactSupport')}</Link>.
              </Typography>
            </>
          )}

          {!shouldRenderContactSupport && selected.url && (
            <>
              <Typography variant='body2'>
                {t('addAccountModal.needHelp')}{' '}
                <Link
                  href={selected.url}
                  target='_blank'
                >
                  {t('addAccountModal.visitSite')}
                </Link>
                .
              </Typography>
            </>
          )}

          {shouldRenderSecurityLink && securityLink && (
            <Typography variant='body2'>
              <Link href={securityLink}>
                {t('addAccountModal.learnMoreSecurity')}
              </Link>
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default RecommendedYodleeIntegration;
